import { formatReportingDate } from "./coverageGrid.utils";
import type {
  DocumentIngestionQuery,
  IngestionClassificationState,
  IngestionConfidence,
} from "#graphql/supergraphTypes";

/** One document from the ingestion read model. */
export type IngestionDoc = NonNullable<
  DocumentIngestionQuery["documentIngestion"]
>["documents"][number];

/** Selectable doc types, hardcoded FE-side for now (value = fay wire value).
 *  Constrained to fay's canonical classifier vocabulary — a value outside it is
 *  rejected by `reclassifyDocument` and the write fails. Package type is fixed
 *  to "Period close" and has no selector. */
export const DOC_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "pcap", label: "PCAP" },
  { value: "financial_statement", label: "Financial statement" },
  { value: "workbook", label: "Workbook" },
];

/** Canonical types the classifier can emit but a user cannot pick. "unknown"
 *  means classification ran without resolving a type; it needs a label so the
 *  UI never renders the raw wire value. */
const DOC_TYPE_DISPLAY_ONLY_LABELS: Record<string, string> = {
  unknown: "Unknown",
};

/** The package type is fixed for now — there is no selector. */
export const PACKAGE_TYPE = "Period close";

/** Local, per-document override layered over the read model. Reclassify/remove
 *  persist for real, but the screen doesn't refetch documentIngestion after a
 *  write, so user assignments/edits live in this optimistic overlay that drives
 *  the grouped UI in-session; a failed write rolls its entry back. */
export type DocOverride = {
  documentType?: string | null;
  entityId?: string | null;
  entityName?: string | null;
  reportingDate?: string | null;
  /** Set once the user has explicitly classified the doc via a modal. */
  userAssigned?: boolean;
};

/** Read-model classification merged with any local override. */
export type EffectiveDoc = {
  id: string;
  filename: string;
  state: IngestionClassificationState;
  documentType: string | null;
  entityId: string | null;
  entityName: string | null;
  reportingDate: string | null;
  confidence: IngestionConfidence | null;
  failureReason: string | null;
  userAssigned: boolean;
};

export function buildEffectiveDoc(
  doc: IngestionDoc,
  override?: DocOverride,
  entityNames?: Map<string, string>,
): EffectiveDoc {
  const c = doc.classification;
  const entityId = override?.entityId ?? c.entityId ?? null;
  // fay persists only entityId; entityName comes back null on a refetch. Backfill
  // from the arena entity universe so the package title shows the name, not the id.
  const entityName =
    override?.entityName ??
    c.entityName ??
    (entityId ? (entityNames?.get(entityId) ?? null) : null);
  return {
    id: doc.id,
    filename: doc.filename,
    state: c.state,
    documentType: override?.documentType ?? c.documentType ?? null,
    entityId,
    entityName,
    reportingDate: override?.reportingDate ?? c.reportingDate ?? null,
    confidence: c.confidence ?? null,
    failureReason: c.failureReason ?? null,
    userAssigned: override?.userAssigned ?? false,
  };
}

/** A deliverable package: docs sharing an entity + reporting date, plus the
 *  pinned catch-all bucket of docs still needing classification. */
export type PackageGroup = {
  key: string;
  entityId: string | null;
  entityName: string | null;
  reportingDate: string | null;
  docs: EffectiveDoc[];
  unclassified: boolean;
};

/** A doc surfaces in the "Not classified" bucket when it is missing either of
 *  the two package-identity fields — entity and reporting date — or is a
 *  low-confidence auto-proposal that the user hasn't explicitly assigned.
 *
 *  Document type is deliberately not part of the gate. It describes the file,
 *  not which package the file belongs to, and the assign modal doesn't collect
 *  it, so gating on it would strand a document whose classification never
 *  resolved a type in this bucket with no way out. This mirrors fay's own
 *  approve gate, which requires exactly entity + reporting date; an untyped doc
 *  binds to its own slot and reads "Unclassified type" in the summary. Type
 *  stays editable per document via Edit document type.
 *
 *  A FAILED state with both fields filled is not a review signal on its own —
 *  auto-classification failures never populate them, so their presence can only
 *  come from a human reclassify (which the read model persists but doesn't reset
 *  back off FAILED). That keeps an assignment grouped after a slideout
 *  close/reopen drops the in-memory `userAssigned` override. */
function needsReview(doc: EffectiveDoc): boolean {
  if (!doc.entityId || !doc.reportingDate) {
    return true;
  }
  if (doc.userAssigned) {
    return false;
  }
  return doc.confidence === "LOW";
}

export function groupIntoPackages(docs: EffectiveDoc[]): PackageGroup[] {
  const groups = new Map<string, PackageGroup>();
  const unclassified: EffectiveDoc[] = [];
  for (const doc of docs) {
    if (needsReview(doc)) {
      unclassified.push(doc);
    } else {
      const key = `${doc.entityId}::${doc.reportingDate}`;
      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          entityId: doc.entityId,
          entityName: doc.entityName,
          reportingDate: doc.reportingDate,
          docs: [],
          unclassified: false,
        };
        groups.set(key, group);
      }
      group.docs.push(doc);
    }
  }
  const ordered: PackageGroup[] = [];
  // Pin the "Not classified" warning bucket to the top.
  if (unclassified.length) {
    ordered.push({
      key: "::unclassified",
      entityId: null,
      entityName: null,
      reportingDate: null,
      docs: unclassified,
      unclassified: true,
    });
  }
  ordered.push(...groups.values());
  return ordered;
}

/** Accordion title for a classified package: "Period close · <entity> · <date>". */
export function packageTitle(group: PackageGroup): string {
  const entity = group.entityName ?? group.entityId ?? "Unknown entity";
  const date = group.reportingDate ? formatReportingDate(group.reportingDate) : "No date";
  return `${PACKAGE_TYPE} · ${entity} · ${date}`;
}

/** File glyph: spreadsheets get the excel icon, everything else the PDF icon. */
export function fileIconType(filename: string): "file-excel" | "file-pdf" {
  return filename.toLowerCase().endsWith(".xlsx") ? "file-excel" : "file-pdf";
}

/** Human label for a doc-type wire value, falling back to the raw value. */
export function docTypeLabel(value: string | null): string | null {
  if (!value) {
    return null;
  }
  return (
    DOC_TYPE_OPTIONS.find((o) => o.value === value)?.label ??
    DOC_TYPE_DISPLAY_ONLY_LABELS[value] ??
    value
  );
}
